import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import {
  ArrowBack,
  VolumeUp,
  Menu,
  NavigateNext,
  NavigateBefore,
} from "@mui/icons-material";


const LessonDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [wordListOpen, setWordListOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showMeaning, setShowMeaning] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);

  useEffect(() => {
    const savedLessons = JSON.parse(localStorage.getItem("lessons") || "[]");
    const currentLesson = savedLessons.find((l) => l.id === id);
    if (currentLesson) {
      setLesson(currentLesson);
    } else {
      navigate("/");
    }
  }, [id, navigate]);

  const handlePlayAudio = useCallback(
    (word) => {
      try {
        const currentWord = lesson.words.find((w) => w.word === word);
        if (currentWord?.audioUrl) {
          const audio = new Audio(currentWord.audioUrl);
          audio.play();
        }
      } catch (error) {
        console.error("Error playing audio:", error);
      }
    },
    [lesson]
  );

  const handleNext = useCallback(() => {
    if (currentIndex < lesson.words.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowMeaning(false);
    }
  }, [currentIndex, lesson?.words.length]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowMeaning(false);
    }
  }, [currentIndex]);

  const handleFlip = useCallback(() => {
    if (isFlipping) return;
    setIsFlipping(true);
    setShowMeaning(!showMeaning);
    setTimeout(() => {
      setIsFlipping(false);
    }, 600);
  }, [isFlipping, showMeaning]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!lesson || isFlipping) return;

      switch (event.key) {
        case "ArrowLeft":
          if (currentIndex > 0) {
            handlePrevious();
          }
          break;
        case "ArrowRight":
          if (currentIndex < lesson.words.length - 1) {
            handleNext();
          }
          break;
        case "ArrowUp":
        case "ArrowDown":
          handleFlip();
          break;
        case " ":
          handlePlayAudio(lesson.words[currentIndex].word);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [
    lesson,
    currentIndex,
    showMeaning,
    isFlipping,
    handleFlip,
    handleNext,
    handlePlayAudio,
    handlePrevious,
  ]);

  if (!lesson) return null;

  const currentWord = lesson.words[currentIndex];

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <IconButton onClick={() => navigate("/")} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            {lesson.name}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setWordListOpen(true)}
            sx={{ mr: 2 }}
          >
            <Menu sx={{ mr: 1 }} />
            Danh sách từ
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/lesson/${lesson.id}/test`)}
            sx={{ mr: 2 }}
          >
            Kiểm tra
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/lesson/${lesson.id}/edit`)}
          >
            Chỉnh sửa
          </Button>
        </Box>

        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 400,
                height: 300,
                position: "relative",
                perspective: "1000px",
                mx: "auto",
                mb: 4,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  transformStyle: "preserve-3d",
                  transition: "transform 0.6s",
                  transform: showMeaning ? "rotateY(180deg)" : "rotateY(0deg)",
                }}
              >
                <Card
                  sx={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                    cursor: "pointer",
                    boxShadow: 3,
                    "&:hover": {
                      boxShadow: 6,
                    },
                  }}
                  onClick={handleFlip}
                >
                  <CardContent
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 4,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Typography
                        variant="h3"
                        sx={{ flexGrow: 1, textAlign: "center" }}
                      >
                        {currentWord.word}
                      </Typography>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayAudio(currentWord.word);
                        }}
                        sx={{
                          position: "absolute",
                          right: 16,
                          top: 16,
                        }}
                      >
                        <VolumeUp />
                      </IconButton>
                    </Box>
                    <Typography variant="h6" color="text.secondary">
                      {currentWord.pronunciation}
                    </Typography>
                  </CardContent>
                </Card>
                <Card
                  sx={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                    transform: "rotateY(180deg)",
                    cursor: "pointer",
                    boxShadow: 3,
                    "&:hover": {
                      boxShadow: 6,
                    },
                  }}
                  onClick={handleFlip}
                >
                  <CardContent
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 4,
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{ mb: 2, textAlign: "center" }}
                    >
                      {currentWord.meaning}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {currentWord.type}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <IconButton
                onClick={handlePrevious}
                disabled={currentIndex === 0 || isFlipping}
                sx={{
                  position: "absolute",
                  left: -60,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "background.paper",
                  boxShadow: 1,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor: "background.paper",
                    boxShadow: 3,
                    transform: "translateY(-50%) scale(1.1)",
                  },
                }}
              >
                <NavigateBefore />
              </IconButton>
              <IconButton
                onClick={handleNext}
                disabled={
                  currentIndex === lesson.words.length - 1 || isFlipping
                }
                sx={{
                  position: "absolute",
                  right: -60,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "background.paper",
                  boxShadow: 1,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor: "background.paper",
                    boxShadow: 3,
                    transform: "translateY(-50%) scale(1.1)",
                  },
                }}
              >
                <NavigateNext />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {currentIndex + 1} / {lesson.words.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Phím tắt: ← → (chuyển từ), ↑ ↓ (lật card), Space (phát âm)
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Dialog
        open={wordListOpen}
        onClose={() => setWordListOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Danh sách từ vựng
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lesson.words.length} từ
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {lesson.words.map((word, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                          {word.word}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handlePlayAudio(word.word)}
                        >
                          <VolumeUp />
                        </IconButton>
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >
                          {word.meaning}
                        </Typography>
                        <br />
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          {word.pronunciation} - {word.type}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                {index < lesson.words.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default LessonDetail;
